import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Tables } from '@/integrations/supabase/types';

type Profile = Tables<'profiles'>;
type Membership = Tables<'memberships'>;
type Group = Tables<'groups'>;

// Explicit challenge status model
export type ChallengeStatus = 'scheduled' | 'active' | 'finished' | null;

// Helper to derive challenge status from group dates
export function deriveChallengeStatus(group: Group | null): ChallengeStatus {
  if (!group) return null;
  
  const now = new Date();
  const start = new Date(group.challenge_start);
  const end = new Date(group.challenge_end);
  
  if (now < start) return 'scheduled';
  if (now > end) return 'finished';
  return 'active';
}

interface CompletedChallengeData {
  group: Group;
  totalDrinks: number;
  longestStreak: number;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  membership: Membership | null;
  group: Group | null;
  challengeStatus: ChallengeStatus;
  completedChallenge: CompletedChallengeData | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  refreshMembership: () => Promise<void>;
  clearCompletedChallenge: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [membership, setMembership] = useState<Membership | null>(null);
  const [group, setGroup] = useState<Group | null>(null);
  const [completedChallenge, setCompletedChallenge] = useState<CompletedChallengeData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (userId: string) => {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();
    setProfile(data);
    return data;
  };

  const fetchMembership = async (userId: string) => {
    // Use the backend resolver RPC - single source of truth for current challenge
    // This handles all edge cases and is deterministic
    const { data: currentChallenge, error: resolveError } = await supabase
      .rpc('resolve_current_challenge');

    if (!resolveError && currentChallenge && currentChallenge.length > 0) {
      const challenge = currentChallenge[0];
      
      // Build membership object from resolver result
      const membershipData: Membership = {
        id: challenge.membership_id,
        user_id: userId,
        group_id: challenge.group_id,
        role: challenge.role,
        joined_at: challenge.joined_at,
      };

      // Build group object from resolver result
      const groupData: Group = {
        id: challenge.group_id,
        invite_code: challenge.invite_code,
        challenge_start: challenge.challenge_start,
        challenge_end: challenge.challenge_end,
        status: 'active',
        created_at: challenge.joined_at, // Use joined_at as fallback
      };

      setCompletedChallenge(null);
      setMembership(membershipData);
      setGroup(groupData);
      return membershipData;
    }

    // No active/scheduled challenge found via RPC
    // Check for recently completed challenges to show the summary
    const { data: allMemberships } = await supabase
      .from('memberships')
      .select('*, groups(*)')
      .eq('user_id', userId)
      .order('joined_at', { ascending: false });

    if (allMemberships && allMemberships.length > 0) {
      const now = new Date();
      
      // Find the most recently finished challenge
      const finishedMembership = allMemberships.find(m => {
        const g = m.groups as unknown as Group | null;
        if (!g) return false;
        return g.status === 'active' && new Date(g.challenge_end) <= now;
      });

      if (finishedMembership && finishedMembership.groups) {
        const groupData = finishedMembership.groups as unknown as Group;
        
        // Fetch stats for completed challenge summary
        const { data: drinkLogs } = await supabase
          .from('drink_logs')
          .select('logged_at')
          .eq('user_id', userId)
          .eq('group_id', groupData.id);
        
        const totalDrinks = drinkLogs?.length || 0;
        
        // Calculate longest streak
        let longestStreak = 0;
        const start = new Date(groupData.challenge_start);
        start.setHours(0, 0, 0, 0);
        const end = new Date(groupData.challenge_end);
        end.setHours(0, 0, 0, 0);
        
        if (!drinkLogs || drinkLogs.length === 0) {
          longestStreak = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
        } else {
          const sortedLogs = [...drinkLogs].sort((a, b) => 
            new Date(a.logged_at).getTime() - new Date(b.logged_at).getTime()
          );
          
          const firstDrink = new Date(sortedLogs[0].logged_at);
          firstDrink.setHours(0, 0, 0, 0);
          longestStreak = Math.max(longestStreak, Math.floor((firstDrink.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
          
          for (let i = 0; i < sortedLogs.length - 1; i++) {
            const current = new Date(sortedLogs[i].logged_at);
            current.setHours(0, 0, 0, 0);
            const next = new Date(sortedLogs[i + 1].logged_at);
            next.setHours(0, 0, 0, 0);
            const gap = Math.floor((next.getTime() - current.getTime()) / (1000 * 60 * 60 * 24)) - 1;
            longestStreak = Math.max(longestStreak, gap);
          }
          
          const lastDrink = new Date(sortedLogs[sortedLogs.length - 1].logged_at);
          lastDrink.setHours(0, 0, 0, 0);
          longestStreak = Math.max(longestStreak, Math.floor((end.getTime() - lastDrink.getTime()) / (1000 * 60 * 60 * 24)));
        }
        
        setCompletedChallenge({ group: groupData, totalDrinks, longestStreak });
      } else {
        setCompletedChallenge(null);
      }
    } else {
      setCompletedChallenge(null);
    }

    // No active membership
    setMembership(null);
    setGroup(null);
    return null;
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  };

  const refreshMembership = async () => {
    if (user) {
      await fetchMembership(user.id);
    }
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          setTimeout(() => {
            fetchProfile(session.user.id);
            fetchMembership(session.user.id);
          }, 0);
        } else {
          setProfile(null);
          setMembership(null);
          setGroup(null);
          setCompletedChallenge(null);
        }
        
        setLoading(false);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchProfile(session.user.id);
        fetchMembership(session.user.id);
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setMembership(null);
    setGroup(null);
    setCompletedChallenge(null);
  };

  const clearCompletedChallenge = () => {
    setCompletedChallenge(null);
  };

  // Derive challenge status from group - memoized
  const challengeStatus = useMemo(() => deriveChallengeStatus(group), [group]);

  return (
    <AuthContext.Provider value={{
      user,
      session,
      profile,
      membership,
      group,
      challengeStatus,
      completedChallenge,
      loading,
      signOut,
      refreshProfile,
      refreshMembership,
      clearCompletedChallenge,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
